/* ────────────────────────────────────────
   한글 유사 검색 — 자모 분해 + 초성 매칭 (순수 함수)
   효신님 2026-06-10: "ㅅ → 사 → 삼" 타이핑 도중에도 '삼성전자'가 떠야 함.
   - 조합 중 음절: 쿼리·대상을 자모열로 분해해 substring 매칭 ("사"=ㅅㅏ ⊂ "삼"=ㅅㅏㅁ)
   - 초성 검색: 쿼리가 전부 초성(ㄱ~ㅎ)이면 대상의 초성열과 매칭 ("ㅅㅈ" → 삼성전자)
   - 비한글(영문·숫자)은 소문자 그대로 — 기존 symbol 검색과 결합해 사용
   - TDD: koSearch.test.ts (feedback_tdd_auto)
──────────────────────────────────────── */

const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const JUNG = ['ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅘ', 'ㅙ', 'ㅚ', 'ㅛ', 'ㅜ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅠ', 'ㅡ', 'ㅢ', 'ㅣ'];
const JONG = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

const HANGUL_BASE = 0xac00; // '가'
const isSyllable = (code: number) => code >= 0xac00 && code <= 0xd7a3;
const isJamoCho = (ch: string) => CHO.includes(ch);

/** 음절열 → 자모열 ("삼성" → "ㅅㅏㅁㅅㅓㅇ"). 비한글은 소문자 그대로 */
export function decomposeKo(str: string): string {
  let out = '';
  for (const ch of str) {
    const code = ch.charCodeAt(0);
    if (isSyllable(code)) {
      const i = code - HANGUL_BASE;
      out += CHO[Math.floor(i / 588)] + JUNG[Math.floor((i % 588) / 28)] + JONG[i % 28];
    } else {
      out += ch.toLowerCase();
    }
  }
  return out;
}

/** 음절열 → 초성열 ("삼성전자" → "ㅅㅅㅈㅈ"). 비한글은 소문자 그대로 */
export function choseongOf(str: string): string {
  let out = '';
  for (const ch of str) {
    const code = ch.charCodeAt(0);
    out += isSyllable(code) ? CHO[Math.floor((code - HANGUL_BASE) / 588)] : ch.toLowerCase();
  }
  return out;
}

/** 쿼리가 전부 초성 자모인지 ("ㅅㅈ" true / "삼" false / "s" false) */
export function isChoseongQuery(q: string): boolean {
  if (!q) return false;
  for (const ch of q) if (!isJamoCho(ch)) return false;
  return true;
}

/**
 * 한글 유사 매칭 — 초성 쿼리는 초성열로, 그 외엔 자모 분해 substring으로.
 * 빈 쿼리는 항상 true (필터 미적용).
 */
export function koMatch(name: string, query: string): boolean {
  const q = query.trim();
  if (!q) return true;
  if (isChoseongQuery(q)) return choseongOf(name).includes(q);
  return decomposeKo(name).includes(decomposeKo(q));
}
